import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config';
import type { CallQueueItem } from '../types';
import { usePolling } from './usePolling';

interface SSEMessage {
  type: 'connected' | 'queue_update' | 'error';
  agent_id?: string;
  queue?: CallQueueItem[];
  timestamp?: string;
  message?: string;
}

export const useCallQueueSSE = (agentId: string) => {
  const [queue, setQueue] = useState<CallQueueItem[]>([]);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<string | null>(null);

  const pendingRequestRef = useRef<AbortController | null>(null);
  const requestInFlightRef = useRef(false);

  const normalizeAgentId = (value?: string | null) => String(value ?? '').trim().toLowerCase();
  const isAssignedWaitingCall = (item: CallQueueItem) =>
    item.status === 'waiting' && normalizeAgentId(item.assignedAgentId) === normalizeAgentId(agentId);
  const filterQueueForAgent = (items: CallQueueItem[]) => items.filter(isAssignedWaitingCall);

  const handleSSEData = useCallback(
    (data: string) => {
      setLastEvent(new Date().toISOString());

      try {
        const message: SSEMessage = JSON.parse(data);

        if (message.type === 'connected') {
        } else if (message.type === 'queue_update' && message.queue) {
          const filtered = filterQueueForAgent(message.queue);
          setQueue(filtered);
        } else if (message.type === 'error') {
          console.error('[SSE_CLIENT] ERROR MESSAGE:', message.message);
          setError(message.message || 'Unknown error');
        }
      } catch (err) {
        console.error('[SSE_CLIENT] FAILED TO PARSE MESSAGE:', err);
        console.error('[SSE_CLIENT] RAW DATA:', data);
      }
    },
    [agentId, filterQueueForAgent, isAssignedWaitingCall],
  );

  const abortPendingRequest = useCallback(() => {
    if (pendingRequestRef.current) {
      pendingRequestRef.current.abort();
      pendingRequestRef.current = null;
    }
    requestInFlightRef.current = false;
  }, []);

  const pollQueue = useCallback(async () => {
      if (!agentId) return;

      if (requestInFlightRef.current) {
        return;
      }

      const controller = new AbortController();
      pendingRequestRef.current = controller;
      requestInFlightRef.current = true;

      try {
        const res = await fetch(`${API_BASE_URL}/api/call/queue?agent_id=${encodeURIComponent(agentId)}`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.queue)) {
          const filtered = filterQueueForAgent(data.queue);
          setQueue(filtered);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[QUEUE_POLL] Failed:', err);
          setConnectionState('disconnected');
        }
      } finally {
        if (pendingRequestRef.current === controller) {
          pendingRequestRef.current = null;
        }
        requestInFlightRef.current = false;
      }
    },
    [agentId],
  );

  usePolling(() => pollQueue(), 5 * 60_000, [agentId, pollQueue, connectionState], {
    enabled: Boolean(agentId) && connectionState === 'disconnected',
    immediate: true,
    pauseWhenHidden: true,
  });

  useEffect(() => {
    if (!agentId) {
      return;
    }

    // --- SSE connection ---
    const sseUrl = API_ENDPOINTS.SSE.QUEUE(agentId);
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      setConnectionState('connected');
      setError(null);
    };

    eventSource.addEventListener('connected', (event) => {
      const data = (event as MessageEvent).data;
      handleSSEData(data);
    });

    eventSource.addEventListener('queue_update', (event) => {
      const data = (event as MessageEvent).data;
      handleSSEData(data);
    });

    eventSource.addEventListener('error', (event) => {
      const data = (event as MessageEvent).data;
      if (typeof data === 'string' && data.trim()) {
        handleSSEData(data);
      }
    });

    eventSource.onmessage = (event) => {
      handleSSEData(event.data);
    };

    eventSource.onerror = (err) => {
      console.error('SSE: CONNECTION ERROR:', err);
      setConnectionState('disconnected');
      setError('Connection error');
    };

    return () => {
      eventSource.close();
      setConnectionState('disconnected');
      abortPendingRequest();
    };
  }, [agentId, abortPendingRequest]);

  const getWaitingCalls = useCallback(() => {
    const waiting = queue.filter(item => item.status === 'waiting');
    return waiting;
  }, [queue]);

  return {
    queue,
    waitingCalls: getWaitingCalls(),
    isConnected: connectionState === 'connected',
    connectionState,
    error,
    lastEvent,
  };
};
